// src/components/Vendors.js — Bookkeeping → Vendors
// [marker: vendors v1]
// <Vendors canEdit />                       the vendor list (grid) with add / edit / history
// <VendorModal vendor? initialName? onClose onSaved />   add or edit one vendor (also used from the register)
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from './DataGrid';
import { AccountSelect } from './AccountSelect';
import { BillModal, BillPaymentModal } from './Bills'; // [marker: bills v1]

const money = (n) => `${parseFloat(n || 0) < 0 ? '-' : ''}$${Math.abs(parseFloat(n || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const KIND = { check: 'Check', bank_deposit: 'Deposit', charge: 'Card charge', cc_credit: 'Card credit', transfer: 'Transfer', journal: 'Journal', bill_payment: 'Bill payment' };
const EXPENSE_TYPES = ['expense', 'other_expense', 'cost_of_goods_sold', 'fixed_asset'];

export default function Vendors({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState('true'); // 'true' | 'false' | 'all'
  const [editing, setEditing] = useState(null); // vendor | 'new'

  const load = useCallback(() => {
    setLoading(true);
    api.get('/vendors?active=all').then(r => setRows(r.data || []))
      .catch(() => toast.error('Could not load vendors.')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const shown = rows.filter(v => (show === 'all' ? true : show === 'true' ? v.is_active : !v.is_active));
  const columns = [
    { key: 'name', label: 'Vendor', width: 220, render: v => <strong>{v.name}</strong> },
    { key: 'contact_name', label: 'Contact', width: 150 },
    { key: 'phone', label: 'Phone', width: 120 },
    { key: 'email', label: 'Email', width: 190 },
    { key: 'default_account_name', label: 'Usual Expense Account', width: 190 },
    { key: 'open_balance', label: 'Unpaid Bills', width: 115, type: 'money', align: 'right' }, // [marker: bills v1]
    { key: 'paid_this_year', label: 'Paid This Year', width: 120, type: 'money', align: 'right' },
    { key: 'paid_total', label: 'Paid (All Time)', width: 120, type: 'money', align: 'right', defaultHidden: true },
    { key: 'txn_count', label: 'Transactions', width: 100, type: 'number' },
    { key: 'last_txn_date', label: 'Last Transaction', width: 120, type: 'date' },
    { key: 'payment_terms', label: 'Terms', width: 90, defaultHidden: true },
    { key: 'account_number', label: 'Our Account #', width: 120, defaultHidden: true },
    { key: 'city', label: 'City', width: 110, defaultHidden: true },
    { key: 'is_1099', label: '1099', width: 65, type: 'bool', defaultHidden: true },
    { key: 'is_active', label: 'Active', width: 70, type: 'bool' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--gray-600)' }}>
          Everyone you pay. Pick a vendor when you write a check or record a card charge in a register, and it shows here.
        </p>
        {canEdit && <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>+ Add Vendor</button>}
      </div>
      <DataGrid
        gridKey="vendors"
        rows={shown}
        loading={loading}
        columns={columns}
        onRowClick={v => setEditing(v)}
        emptyText={rows.length ? 'No vendors match.' : 'No vendors yet — add the companies and people you pay.'}
        toolbarLeft={(
          <select className="form-select" style={{ width: 150 }} value={show} onChange={e => setShow(e.target.value)}>
            <option value="true">Active vendors</option>
            <option value="false">Inactive vendors</option>
            <option value="all">All vendors</option>
          </select>
        )}
      />
      {editing && (
        <VendorModal vendor={editing === 'new' ? null : editing} canEdit={canEdit}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

export function VendorModal({ vendor, initialName, canEdit = true, onClose, onSaved, zIndex = 1500 }) {
  const isEdit = !!vendor;
  const [tab, setTab] = useState('details');
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [billOpen, setBillOpen] = useState(null); // [marker: bills v1] id | 'new'
  const [pmtOpen, setPmtOpen] = useState(null);
  const [form, setForm] = useState(() => ({
    name: vendor?.name || initialName || '', contactName: vendor?.contact_name || '', phone: vendor?.phone || '', email: vendor?.email || '',
    website: vendor?.website || '', accountNumber: vendor?.account_number || '',
    addressLine1: vendor?.address_line1 || '', addressLine2: vendor?.address_line2 || '', city: vendor?.city || '', state: vendor?.state || '', zip: vendor?.zip || '',
    paymentTerms: vendor?.payment_terms || '', notes: vendor?.notes || '',
    defaultAccountId: vendor?.default_account_id || null, is1099: !!vendor?.is_1099, isActive: vendor ? vendor.is_active !== false : true,
  }));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadDetail = useCallback(() => {
    if (!isEdit) return;
    api.get(`/vendors/${vendor.id}`).then(r => setDetail(r.data)).catch(() => {});
  }, [isEdit, vendor]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Enter the vendor name.');
    setSaving(true);
    try {
      const r = isEdit ? await api.patch(`/vendors/${vendor.id}`, form) : await api.post('/vendors', form);
      toast.success(isEdit ? 'Vendor saved.' : 'Vendor added.');
      onSaved && onSaved(r.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${vendor.name}?`)) return;
    try { await api.delete(`/vendors/${vendor.id}`); toast.success('Vendor deleted.'); onSaved && onSaved(null); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not delete.'); }
  };

  const txns = detail?.transactions || [];
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const paidYear = txns.filter(t => t.date >= yearStart).reduce((s, t) => s + parseFloat(t.amount || 0), 0);
  const paidAll = txns.reduce((s, t) => s + parseFloat(t.amount || 0), 0);
  const ro = !canEdit;
  const F = ({ label, k, type = 'text', span = 1, ...rest }) => (
    <div className="form-group" style={{ gridColumn: `span ${span}` }}>
      <label className="form-label">{label}</label>
      <input className="form-input" type={type} value={form[k]} disabled={ro} onChange={e => set(k, e.target.value)} {...rest} />
    </div>
  );

  return (
    <>
    <div className="modal-overlay" onClick={onClose} style={{ zIndex }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 820 }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? vendor.name : 'Add Vendor'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        {isEdit && (
          <div className="tabs" style={{ padding: '0 20px', marginBottom: 0 }}>
            <button className={`tab-btn${tab === 'details' ? ' active' : ''}`} onClick={() => setTab('details')}>Details</button>
            <button className={`tab-btn${tab === 'bills' ? ' active' : ''}`} onClick={() => setTab('bills')}>
              Bills {detail ? <span style={{ color: 'var(--gray-400)' }}>({(detail.bills || []).filter(b => parseFloat(b.balance) > 0).length} unpaid)</span> : null}
            </button>
            <button className={`tab-btn${tab === 'txns' ? ' active' : ''}`} onClick={() => setTab('txns')}>
              Transactions {detail ? <span style={{ color: 'var(--gray-400)' }}>({txns.length})</span> : null}
            </button>
          </div>
        )}
        <div className="modal-body" style={{ maxHeight: '68vh', overflow: 'auto' }}>
          {tab === 'details' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 12px' }}>
              {F({ label: 'Vendor Name *', k: 'name', span: 2, autoFocus: !isEdit })}
              {F({ label: 'Contact Person', k: 'contactName', span: 2 })}
              {F({ label: 'Phone', k: 'phone' })}
              {F({ label: 'Email', k: 'email', type: 'email', span: 2 })}
              {F({ label: 'Website', k: 'website' })}
              {F({ label: 'Address', k: 'addressLine1', span: 2 })}
              {F({ label: 'Address Line 2', k: 'addressLine2', span: 2 })}
              {F({ label: 'City', k: 'city', span: 2 })}
              {F({ label: 'State', k: 'state' })}
              {F({ label: 'ZIP', k: 'zip' })}
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Usual Expense Account</label>
                <AccountSelect value={form.defaultAccountId} disabled={ro} types={EXPENSE_TYPES} placeholder="— None —"
                  onChange={v => set('defaultAccountId', v)} />
                <p className="form-hint">Filled in for you when you pick this vendor in a register.</p>
              </div>
              {F({ label: 'Our Account # With Them', k: 'accountNumber' })}
              {F({ label: 'Payment Terms', k: 'paymentTerms', placeholder: 'e.g. Net 30' })}
              <div className="form-group" style={{ gridColumn: 'span 4' }}>
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2} value={form.notes} disabled={ro} onChange={e => set('notes', e.target.value)} />
              </div>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.875rem', gridColumn: 'span 2' }}>
                <input type="checkbox" checked={form.is1099} disabled={ro} onChange={e => set('is1099', e.target.checked)} /> Gets a 1099
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.875rem', gridColumn: 'span 2' }}>
                <input type="checkbox" checked={form.isActive} disabled={ro} onChange={e => set('isActive', e.target.checked)} /> Active
                <span className="form-hint" style={{ margin: 0 }}>(inactive vendors are hidden from the pick list)</span>
              </label>
            </div>
          ) : tab === 'bills' ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, fontSize: '0.9rem' }}>
                <span>Unpaid: <strong>{money((detail?.bills || []).reduce((s, b) => s + parseFloat(b.balance || 0), 0))}</strong></span>
                {canEdit && <button className="btn btn-primary btn-sm" onClick={() => setBillOpen('new')}>+ Enter Bill</button>}
              </div>
              {!detail ? <div className="spinner" /> : (detail.bills || []).length === 0 ? (
                <p style={{ color: 'var(--gray-500)' }}>No bills from this vendor yet.</p>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Bill Date</th><th>Ref. No.</th><th>Due</th><th>Memo</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Still Owed</th></tr></thead>
                    <tbody>
                      {detail.bills.map(b => (
                        <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => setBillOpen(b.id)}>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDay(b.bill_date)}</td><td>{b.bill_number}</td><td style={{ whiteSpace: 'nowrap' }}>{b.due_date ? fmtDay(b.due_date) : '—'}</td>
                          <td>{b.notes}</td><td style={{ textAlign: 'right' }}>{money(b.total_amount)}</td>
                          <td style={{ textAlign: 'right', fontWeight: parseFloat(b.balance) > 0 ? 700 : 400, color: parseFloat(b.balance) > 0 ? undefined : 'var(--success)' }}>{parseFloat(b.balance) > 0 ? money(b.balance) : 'Paid'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="form-hint">To pay bills, use Bookkeeping → Bills &amp; Checks → Pay Bills (or open a bill and press “Pay This Bill”).</p>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 24, marginBottom: 10, fontSize: '0.9rem' }}>
                <span>Paid this year: <strong>{money(paidYear)}</strong></span>
                <span>All time: <strong>{money(paidAll)}</strong></span>
              </div>
              {!detail ? <div className="spinner" /> : txns.length === 0 ? (
                <p style={{ color: 'var(--gray-500)' }}>No transactions yet. Pick this vendor in “Pay To / Name” when you write a check or record a card charge.</p>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Date</th><th>Type</th><th>No.</th><th>Paid From</th><th>Account</th><th>Memo</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                    <tbody>
                      {txns.map(t => (
                        <tr key={t.id} style={{ cursor: t.reference_type === 'bill_payment' ? 'pointer' : undefined }}
                            onClick={() => { if (t.reference_type === 'bill_payment') setPmtOpen(t.reference_id); }}>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDay(t.date)}</td>
                          <td>{KIND[t.kind] || KIND[t.reference_type] || t.reference_type || 'Entry'}</td>
                          <td>{t.number}</td>
                          <td>{t.register_account || '—'}</td>
                          <td>{t.accounts || '—'}</td>
                          <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.memo || ''}>{t.memo}</td>
                          <td style={{ textAlign: 'right', color: parseFloat(t.amount) < 0 ? 'var(--success)' : undefined }}>{money(t.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="form-hint">Negative amounts are refunds or credits from the vendor. To change a transaction, open it from its register.</p>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <span>{isEdit && canEdit && <button className="btn btn-ghost" style={{ color: 'var(--danger)' }} onClick={remove}>Delete</button>}</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</button>
            {canEdit && <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save' : 'Add Vendor'}</button>}
          </span>
        </div>
      </div>
    </div>
      {billOpen && <BillModal billId={billOpen === 'new' ? null : billOpen} vendorId={vendor?.id} canEdit={canEdit}
        onClose={() => setBillOpen(null)} onSaved={() => { setBillOpen(null); loadDetail(); }} onOpenPayment={(id) => { setBillOpen(null); setPmtOpen(id); }} />}
      {pmtOpen && <BillPaymentModal paymentId={pmtOpen} canEdit={canEdit} onClose={() => setPmtOpen(null)} onSaved={() => { setPmtOpen(null); loadDetail(); }} />}
    </>
  );
}
